import {
  fetchCustomerProducts,
  fetchResellerShopProducts,
  fetchShopCategories,
  fetchShopProductLabels,
  fetchShopProducts
} from "../services/api";
import { setCatalogError, setCatalogLoading, setCategories, setLabels, setProducts } from "../store/slices/product-slice";
import { normalizeShopCategory, normalizeShopProduct } from "./aa-products";

/**
 * Shared by the boot-time catalog load in App.js and any later revalidation
 * (route entry, tab-focus regain). `silent` is what makes those two safe to
 * share: a background revalidation must never flip on the full-screen loading
 * state, and on failure must never call setCatalogError, since that reducer
 * wipes `products` to `[]` -- fine for a first load, but it would blank an
 * already-working shop over a transient network blip.
 */
export async function loadShopCatalog({ dispatch, role, resellerAdminUserId, silent = false }) {
  if (!silent) {
    dispatch(setCatalogLoading(true));
  }

  const isCustomerWithoutReseller = role === "user" && !resellerAdminUserId;

  if (isCustomerWithoutReseller) {
    try {
      const categoriesResponse = await fetchShopCategories();
      dispatch(setCategories((categoriesResponse?.data || []).map(normalizeShopCategory)));
    } catch (_categoriesError) {
      if (!silent) {
        dispatch(setCategories([]));
      }
    }
    if (!silent) {
      dispatch(
        setCatalogError(
          "Your account isn't linked to a reseller yet. Please use the invitation link from your reseller to access products."
        )
      );
    }
    return;
  }

  const productRequest =
    role === "user"
      ? fetchCustomerProducts
      : role === "reseller_admin"
        ? fetchResellerShopProducts
        : fetchShopProducts;
  const canBuy = role === "user" || role === "reseller_admin";
  const wholesaleCatalog = role === "reseller_admin";

  const categoriesTask = fetchShopCategories()
    .then((response) => {
      dispatch(setCategories((response?.data || []).map(normalizeShopCategory)));
    })
    .catch(() => {
      if (!silent) {
        dispatch(setCategories([]));
      }
    });
  const labelsTask = fetchShopProductLabels()
    .then((response) => {
      dispatch(setLabels(response?.data || []));
    })
    .catch(() => {
      if (!silent) {
        dispatch(setLabels([]));
      }
    });

  try {
    const productsResponse = await productRequest();
    dispatch(setProducts((productsResponse?.data || []).map((product) => normalizeShopProduct(product, { canBuy, wholesaleCatalog }))));
    await Promise.all([categoriesTask, labelsTask]);
  } catch (error) {
    if (silent) {
      console.warn("[shop-catalog] silent revalidation failed", error?.message || error);
      return;
    }
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Unable to load the catalog. Please try again later.";
    dispatch(setCatalogError(message));
  }
}
